// Lectura en el navegador de los archivos de inventario y de despacho (Excel o CSV con ; o ,).
// Solo convierte hojas en filas: la validación y la aplicación (todo o nada) se hacen en el servidor.
import * as XLSX from 'xlsx';
import type { DispatchImportRow, InventoryImportRow } from '@teo/shared';
import { normHeader } from './excel';

type Cell = string | number | boolean | Date | null | undefined;
type Row = Cell[];

// Encabezados aceptados, sin acentos ni mayúsculas (español del cliente e inglés).
const HEADERS = {
  location: ['local', 'ubicacion', 'location', 'store', 'tienda', 'bodega'],
  code: ['codigo', 'code', 'ref', 'sku'],
  reference: ['referencia', 'reference'],
  color: ['color', 'colour'],
  size: ['talla', 'size'],
  qty: ['cantidad', 'fisico', 'conteo', 'conteo fisico', 'unidades', 'qty', 'quantity', 'physical', 'units'],
  cost: ['costo', 'costo unitario', 'cost', 'unit cost'],
  price: ['precio_unitario', 'precio unitario', 'precio', 'unit_price', 'unit price', 'price'],
} as const;
type Field = keyof typeof HEADERS;

function fieldOf(cell: Cell): Field | null {
  const h = normHeader(cell);
  if (!h) return null;
  return (Object.keys(HEADERS) as Field[]).find((f) => (HEADERS[f] as readonly string[]).includes(h)) ?? null;
}

/** Busca la fila de encabezado en las primeras 10 filas: debe traer referencia (o código) y cantidad. */
export function detectColumns(rows: Row[]): { start: number; cols: Partial<Record<Field, number>> } | null {
  for (let i = 0; i < Math.min(rows.length, 10); i += 1) {
    const cols: Partial<Record<Field, number>> = {};
    (rows[i] ?? []).forEach((cell, col) => {
      const f = fieldOf(cell);
      if (f && cols[f] === undefined) cols[f] = col;
    });
    if ((cols.code !== undefined || cols.reference !== undefined) && cols.qty !== undefined) return { start: i + 1, cols };
  }
  return null;
}

const text = (v: Cell) => (typeof v === 'number' ? String(Number.isInteger(v) ? v : v) : String(v ?? '').trim());

/** La cantidad viaja tal cual (número o texto) para que el servidor marque el error en su fila. */
function rawNumber(v: Cell): number | string | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return v;
  const s = String(v).trim();
  const n = Number(s.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : s.slice(0, 20);
}

function money(v: Cell): number | null {
  const n = rawNumber(v);
  return typeof n === 'number' && n >= 0 ? n : null;
}

export interface ParsedSheets<T> {
  rows: T[];
  sheets: { name: string; rows: number }[];
  skipped: string[];
  /** true si el archivo trae la columna LOCAL (varias ubicaciones en un archivo). */
  hasLocation: boolean;
}

function readSheets<T>(data: ArrayBuffer | Uint8Array, build: (line: Row, cols: Partial<Record<Field, number>>, rowNo: number) => T | null): ParsedSheets<T> {
  const wb = XLSX.read(data, { type: 'array' });
  const out: ParsedSheets<T> = { rows: [], sheets: [], skipped: [], hasLocation: false };
  for (const name of wb.SheetNames) {
    const ws = wb.Sheets[name];
    if (!ws) continue;
    const rows = XLSX.utils.sheet_to_json<Row>(ws, { header: 1, raw: true, defval: null });
    const header = detectColumns(rows);
    if (!header) {
      out.skipped.push(name);
      continue;
    }
    if (header.cols.location !== undefined) out.hasLocation = true;
    let count = 0;
    for (let r = header.start; r < rows.length; r += 1) {
      const row = build(rows[r] ?? [], header.cols, r + 1);
      if (!row) continue;
      out.rows.push(row);
      count += 1;
    }
    out.sheets.push({ name, rows: count });
  }
  return out;
}

const pick = (line: Row, col: number | undefined): Cell => (col === undefined ? null : line[col]);

/**
 * Modelo de conteo del cliente: LOCAL | REFERENCIA | COLOR | TALLA | CANTIDAD (varias ubicaciones en un archivo),
 * y el formato simple REFERENCIA/CODIGO, [COLOR], [TALLA], CANTIDAD/FISICO, [COSTO].
 */
export function parseInventoryWorkbook(data: ArrayBuffer | Uint8Array): ParsedSheets<InventoryImportRow> {
  return readSheets<InventoryImportRow>(data, (line, cols, rowNo) => {
    const ref = text(pick(line, cols.code ?? cols.reference));
    if (!ref) return null;
    const row: InventoryImportRow = { row: rowNo, ref, qty: rawNumber(pick(line, cols.qty)) };
    const location = text(pick(line, cols.location));
    const color = text(pick(line, cols.color));
    const size = text(pick(line, cols.size));
    if (location) row.location_code = location.toUpperCase();
    if (color) row.color = color;
    if (size) row.size = size;
    const cost = money(pick(line, cols.cost));
    if (cost !== null) row.unit_cost = cost;
    return row;
  });
}

/** Modelo de despacho del cliente: REFERENCIA | CANTIDAD | PRECIO_UNITARIO (opcional) | OBSERVACION, con COLOR y TALLA opcionales. */
export function parseDispatchWorkbook(data: ArrayBuffer | Uint8Array): ParsedSheets<DispatchImportRow> {
  return readSheets<DispatchImportRow>(data, (line, cols, rowNo) => {
    const ref = text(pick(line, cols.code ?? cols.reference));
    const qty = rawNumber(pick(line, cols.qty));
    if (!ref || qty === null) return null; // las filas de ejemplo vacías del modelo se ignoran
    const row: DispatchImportRow = { row: rowNo, ref, qty };
    const color = text(pick(line, cols.color));
    const size = text(pick(line, cols.size));
    if (color) row.color = color;
    if (size) row.size = size;
    const price = money(pick(line, cols.price));
    if (price !== null && price > 0) row.unit_price = price;
    return row;
  });
}

/** Modelo de conteo para descargar (el mismo que está en docs/plantillas). */
export function buildCountTemplate(): Uint8Array {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([
    ['LOCAL', 'REFERENCIA', 'COLOR', 'TALLA', 'CANTIDAD'],
    ['S1', '235802', 'Negro', 'M', 6],
    ['S1', '235683', 'Camel', '', 4],
    ['BOD', 'BOD-008', '', '', 40],
  ]);
  ws['!cols'] = [{ wch: 10 }, { wch: 16 }, { wch: 16 }, { wch: 10 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(wb, ws, 'CONTEO');
  const help = XLSX.utils.aoa_to_sheet([
    ['TEO · MODELO DE CONTEO POR LOCAL'],
    ['LOCAL', 'Código de la ubicación: BOD, S1 … S6. Un archivo puede traer varias ubicaciones.'],
    ['REFERENCIA', 'Código exacto de la referencia en TEO. El archivo no crea productos.'],
    ['COLOR', 'Obligatorio si el producto se controla por color o por color y talla; vacío si se controla por referencia.'],
    ['TALLA', 'Obligatoria solo si el producto se controla por color y talla.'],
    ['CANTIDAD', 'Unidades contadas físicamente. Número entero, 0 o mayor.'],
  ]);
  help['!cols'] = [{ wch: 14 }, { wch: 100 }];
  XLSX.utils.book_append_sheet(wb, help, 'INSTRUCCIONES');
  return new Uint8Array(XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer);
}

/** Bytes del archivo elegido (con FileReader donde File.arrayBuffer no existe). */
export function readFileBytes(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer().then((b) => new Uint8Array(b));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('FileReader'));
    reader.readAsArrayBuffer(file);
  });
}

export function download(bytes: Uint8Array, filename: string) {
  const url = URL.createObjectURL(new Blob([bytes.slice().buffer as ArrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
